
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const DentalImplants = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Dental Implants
  const faqs = [
    {
      question: "What are dental implants?",
      answer: "Dental implants are titanium posts that are surgically placed into the jawbone beneath your gums to replace the roots of missing teeth. Once in place, they allow your dentist to mount replacement teeth onto them, providing a strong foundation for fixed or removable replacement teeth that are made to match your natural teeth."
    },
    {
      question: "Am I a candidate for dental implants?",
      answer: "Most people with good general oral health are candidates for dental implants. You need to have healthy gums and adequate bone to support the implant. At Asrani Dental in Sadar, Nagpur, we conduct a thorough evaluation to determine if implants are right for you, which may include dental X-rays and 3D images."
    },
    {
      question: "How long do dental implants last?",
      answer: "With proper care and maintenance, dental implants can last a lifetime. The crown attached to the implant typically lasts about 10-15 years before it might need replacement due to normal wear and tear. Regular dental check-ups and good oral hygiene practices are essential for the long-term success of dental implants."
    },
    {
      question: "Is the dental implant procedure painful?",
      answer: "The implant procedure is performed under local anesthesia, making the process comfortable with minimal discomfort. Most patients report that implant surgery involves less pain than a tooth extraction. After the procedure, any discomfort can usually be managed with over-the-counter pain medications."
    },
    {
      question: "What is the success rate of dental implants?",
      answer: "Dental implants have a success rate of up to 98%. At Asrani Dental in Nagpur, our experienced team ensures the highest standards of care to maximize the success of your implant treatment. Factors that can affect success include the patient's overall health, bone quality, and oral hygiene practices."
    }
  ];

  // Testimonials for Dental Implants
  const testimonials = [
    {
      name: "Rajesh Sharma",
      comment: "After losing two teeth in an accident, I was concerned about how I would look and eat. Dr. Asrani at Asrani Dental in Sadar, Nagpur recommended dental implants, and I couldn't be happier with the results. They look and feel completely natural, and I can eat anything without worry.",
      rating: 5
    },
    {
      name: "Priya Patel",
      comment: "I had been using dentures for years but was tired of the limitations and discomfort. The team at Asrani Dental Clinic in Nagpur suggested implants, and it has transformed my life. The procedure was much more comfortable than I expected, and now I have teeth that feel like my own again.",
      rating: 5
    },
    {
      name: "Amit Deshmukh",
      comment: "Having dental implants placed at Asrani Dental in Sadar was one of the best decisions I've made. The entire process was explained clearly, the procedure was painless, and the follow-up care was excellent. Now, two years later, my implants are still perfect and I often forget they're not my natural teeth.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle 
        title="Dental Implants in Nagpur | Best Implant Dentist in Sadar – Asrani Dental"
        ogTitle="Asrai Dental - Best Dental Implant Clinic in Nagpur"
        ogDescription="Asrai Dental is the Best Dental Implant Clinic in Nagpur Dental Implants are one of the solutions for missing teeth. Putting the crowns, bridges, or dentures"
        ogUrl="https://asranidental.com/dental-implants/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Dental Implants in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Restore your smile with permanent, natural-looking dental implants at Asrani Dental, Sadar, Nagpur.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/Dental_implant_asrani.jpg" 
                alt="Dental Implants at Asrani Dental Clinic, Sadar, Nagpur" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Permanent Tooth Replacement" 
                subtitle="Dental Implants"
                description="At Asrani Dental in Sadar, Nagpur, our dental implants provide a permanent solution to missing teeth, restoring both function and aesthetics with results that look and feel natural."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Natural Appearance</h3>
                    <p className="text-dental-muted">Dental implants look, feel, and function just like your natural teeth, providing a seamless smile restoration.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Long-lasting Solution</h3>
                    <p className="text-dental-muted">With proper care, implants can last a lifetime, making them a cost-effective option in the long run.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Bone Preservation</h3>
                    <p className="text-dental-muted">Implants stimulate bone growth, preventing the bone loss that typically occurs when teeth are missing.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Improved Comfort</h3>
                    <p className="text-dental-muted">Unlike removable dentures, implants become a part of you, eliminating discomfort and improving speech and eating abilities.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Dental Implant Services" 
            subtitle="Services We Offer"
            description="At Asrani Dental in Sadar, Nagpur, we provide comprehensive implant solutions tailored to your specific needs."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Single Tooth Implants"
              description="Replace an individual missing tooth with a dental implant that looks and functions like your natural tooth without affecting adjacent teeth."
              imageUrl="/lovable-uploads/285554d6-4566-4a60-9073-9f9864390211.png"
            />
            
            <TreatmentServiceCard 
              title="Multiple Tooth Implants"
              description="Replace several missing teeth with implant-supported bridges instead of traditional bridges that require altering healthy teeth."
              imageUrl="/lovable-uploads/f6ace886-837e-4e46-9380-939e9886efaa.png"
            />
            
            <TreatmentServiceCard 
              title="Implant-Supported Dentures"
              description="Secure your dentures with implants for improved stability, comfort, and chewing efficiency compared to conventional dentures."
              imageUrl="/lovable-uploads/1bbf191d-ec90-4f71-9f8e-8391475ee0ea.png"
            />
            
            <TreatmentServiceCard 
              title="All-on-4 Implants"
              description="Revolutionary technique providing a full arch of teeth supported by just four strategically placed implants, often completed in a single day."
              imageUrl="/lovable-uploads/26458846-8adc-4cfd-9f16-a624fe39cbdb.png"
            />
            
            <TreatmentServiceCard 
              title="Bone Grafting"
              description="Procedures to build up the jawbone in areas with insufficient bone volume to support dental implants."
              imageUrl="/lovable-uploads/ea3b5441-00b2-4f4d-a421-8aa463145b7c.png"
            />
            
            <TreatmentServiceCard 
              title="Sinus Lift"
              description="A surgical procedure that adds bone to your upper jaw in the area of your molars and premolars to make it possible to place dental implants."
              imageUrl="/lovable-uploads/03c19580-d6f0-49f6-9a9e-6ca80f87d642.png"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their experiences with dental implant treatments at Asrani Dental in Sadar, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about dental implants at Asrani Dental, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Restore Your Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact Asrani Dental in Sadar, Nagpur today to schedule your implant consultation. Our expert team is ready to help you regain a beautiful, functional smile.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services in Nagpur?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/orthodontics-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/full-mouth-implants-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth Rehabilitation
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Transform Your Smile with Dental Implants"
        description="At Asrani Dental in Sadar, Nagpur, our advanced implant procedures can help you regain confidence with a complete, natural-looking smile. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default DentalImplants;
